import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import { getFileSubmissions, retryFileDelivery, type FileSubmission } from '../services/fileSubmissionService';

const FileSubmissions = () => {
  const [searchParams] = useSearchParams();
  const period = searchParams.get('period') as 'daily' | 'weekly';
  const [submissions, setSubmissions] = useState<FileSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<number | null>(null);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getFileSubmissions(period);
      setSubmissions(response.submissions);
    } catch (err) {
      setError('Failed to fetch file submissions');
      console.error('Error fetching submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
    // Refresh data every minute
    const interval = setInterval(fetchSubmissions, 60000);
    return () => clearInterval(interval);
  }, [period]);

  const handleRetry = async (fileId: number) => {
    try {
      setRetrying(fileId);
      await retryFileDelivery(fileId);
      await fetchSubmissions(); // Refresh the list
    } catch (err) {
      setError('Failed to retry file delivery');
      console.error('Error retrying file delivery:', err);
    } finally {
      setRetrying(null);
    }
  };

  const getStatusColor = (status: FileSubmission['status']) => {
    switch (status) {
      case 'Delivered':
        return 'success';
      case 'Created':
        return 'primary';
      case 'Failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (loading && submissions.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">
          {period === 'daily' ? "Today's" : "This Week's"} File Submissions
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchSubmissions} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Delivered Date</TableCell>
              <TableCell>PBM</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Transactions</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {submissions.map((submission) => (
              <TableRow key={submission.id}>
                <TableCell>{submission.fileName}</TableCell>
                <TableCell>{formatDate(submission.createdDate)}</TableCell>
                <TableCell>{formatDate(submission.deliveredDate)}</TableCell>
                <TableCell>{submission.pbm}</TableCell>
                <TableCell>
                  <Chip 
                    label={submission.status}
                    color={getStatusColor(submission.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">{submission.transactionCount}</TableCell>
                <TableCell align="center">
                  {submission.status === 'Failed' && (
                    <Tooltip title="Retry Delivery">
                      <IconButton 
                        onClick={() => handleRetry(submission.id)}
                        disabled={retrying === submission.id}
                      >
                        {retrying === submission.id ? (
                          <CircularProgress size={24} />
                        ) : (
                          <ReplayIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {submissions.length === 0 && !loading && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No file submissions found for this period.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default FileSubmissions;
